import React, { useEffect, useMemo, useState } from 'react';
import { Button, Popup, Label, Menu, MenuItem } from 'semantic-ui-react';
import { useAppSelector } from '../../../hooks/ReduxHooks';
import { useActions } from '../../../hooks/useActions';
import { setHighlight } from '../../actions';
import { analyticsTrack } from '../../../helpers/AnalyticsUtility';
import { DataLayoutPosition, DataLayoutTab, FieldPosition, TemplatePage } from '../../../types/template-types';
import './ValidationErrorsPopup.scss';
import { ErrorType } from '../../../templateeditor/components/FieldValidations/constants';
import { ViewPosition } from '../../requiredFields';
import ErrorTriangle from '../../../icons/ErrorTriangle';
import WarningTriangle from '../../../icons/WarningTriangle';
import { getErrorFields, getWarningFields, getViewPositionsForFieldIds } from './selectors';

export type ValidationErrorsPopupProps = {
  bringToFieldPosition: (page: TemplatePage, position: FieldPosition) => void;
  bringToDataLayoutPosition: (tab: DataLayoutTab, position: DataLayoutPosition) => void;
};

export default function ValidationErrorsPopup(props: ValidationErrorsPopupProps) {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [hasErrorFields, setHasErrorFields] = useState<boolean>(false);
  const [hasWarningFields, setHasWarningFields] = useState<boolean>(false);
  const [hasMissingRequiredFields, setHasMissingRequiredFields] = useState<boolean>(false);
  const [activeItem, setActiveItem] = useState<string>(`validation-${ErrorType.Error}-menu-item`);
  const formeditorState = useAppSelector((state) => state.formeditor);
  const highlight = formeditorState.highlight;
  const missingRequiredFieldIds = formeditorState.missingRequiredFieldIds;
  const setHighlightAction = useActions(setHighlight);
  const errorFieldIds = getErrorFields(formeditorState).map((field) => field.fieldId);
  const warningFieldIds = getWarningFields(formeditorState).map((field) => field.fieldId);

  let numOfInvalidFields = errorFieldIds?.length + warningFieldIds?.length;
  if (missingRequiredFieldIds?.length > 0) {
    numOfInvalidFields += missingRequiredFieldIds.length;
  }

  const errorFieldsViewPositions = useMemo(() => getViewPositionsForFieldIds(formeditorState, errorFieldIds), [errorFieldIds, formeditorState]);
  const warningFieldsViewPositions = useMemo(() => getViewPositionsForFieldIds(formeditorState, warningFieldIds), [warningFieldIds, formeditorState]);
  const missingRequiredFieldsViewPositions = useMemo(
    () => getViewPositionsForFieldIds(formeditorState, missingRequiredFieldIds),
    [missingRequiredFieldIds, formeditorState]
  );

  useEffect(() => {
    if (hasErrorFields || hasMissingRequiredFields) {
      return setActiveItem(`validation-${ErrorType.Error}-menu-item`);
    }

    if (hasWarningFields) {
      return setActiveItem(`validation-${ErrorType.Warning}-menu-item`);
    }

    return setActiveItem(`validation-${ErrorType.Error}-menu-item`);
  }, []);

  useEffect(() => {
    setHasErrorFields(errorFieldIds?.length > 0);
    setHasWarningFields(warningFieldIds?.length > 0);
    setHasMissingRequiredFields(missingRequiredFieldIds?.length > 0);
  }, [errorFieldIds, warningFieldIds, missingRequiredFieldIds]);

  const handleMenuErrorTypeClick = (_, { name }) => setActiveItem(name);

  const handleErrorPopupClick = () => {
    analyticsTrack('Required Fields Drop Down Triggered', {
      action: dropdownOpen ? 'Close Dropdown' : 'Open Dropdown'
    });
    setDropdownOpen(!dropdownOpen);

    setActiveItem((_) => {
      if (hasErrorFields || hasMissingRequiredFields) {
        return `validation-${ErrorType.Error}-menu-item`;
      }

      if (hasWarningFields) {
        return `validation-${ErrorType.Warning}-menu-item`;
      }
    });
  };

  const handleClick = (viewPosition: ViewPosition) => {
    if (document.body.offsetWidth < 800) {
      setDropdownOpen(false);
    }

    if (viewPosition.fieldPosition != null) {
      props.bringToFieldPosition(viewPosition.page, viewPosition.fieldPosition);
    } else if (viewPosition.dataLayoutPosition != null) {
      props.bringToDataLayoutPosition(viewPosition.tab, viewPosition.dataLayoutPosition);
    }

    analyticsTrack('Required Fields Drop Down Triggered', {
      action: 'Item Clicked'
    });

    setHighlightAction(viewPosition.fieldPosition, viewPosition.dataLayoutPosition);
  };

  const handlePopupClose = () => {
    setDropdownOpen(false);
  };

  // defer rendering the menu unless there are items to render
  const renderMenu = () => {
    return (
      <div className="validation-errors-popup-menu-container">
        <Menu attached="top" widths={2}>
          <MenuItem
            name={`validation-${ErrorType.Error}-menu-item`}
            active={activeItem === `validation-${ErrorType.Error}-menu-item`}
            onClick={handleMenuErrorTypeClick}
            disabled={!hasErrorFields && !hasMissingRequiredFields}
          >
            <ErrorTriangle />
            Errors
          </MenuItem>
          <MenuItem
            name={`validation-${ErrorType.Warning}-menu-item`}
            active={activeItem === `validation-${ErrorType.Warning}-menu-item`}
            onClick={handleMenuErrorTypeClick}
            disabled={!hasWarningFields}
          >
            <WarningTriangle />
            Warnings
          </MenuItem>
        </Menu>
        <div className="validation-errors-list">
          {activeItem === `validation-${ErrorType.Error}-menu-item`
            ? [...renderMenuItems(errorFieldsViewPositions), ...renderMenuItems(missingRequiredFieldsViewPositions, true)]
            : renderMenuItems(warningFieldsViewPositions)}
        </div>
      </div>
    );
  };

  const renderMenuItems = (fieldsViewPositions: ViewPosition[], isRequired = false) => {
    const menuItems = [];

    const renderGroup = (viewPositions, groupType) => {
      let previousGroupName = null;

      for (const viewPosition of viewPositions?.filter((x) => x.groupType === groupType)) {
        if (previousGroupName !== viewPosition.groupName) {
          menuItems.push(
            <div
              key={`${groupType}-${viewPosition.groupName}`}
              className={`header ${groupType === 'unmapped' ? 'unmapped' : ''}`}
              title={viewPosition.groupName}
            >
              {viewPosition.groupName}
            </div>
          );
        }

        menuItems.push(
          <React.Fragment key={viewPosition.field.id}>
            <button
              className={`item${highlight?.fieldId === viewPosition.field.id ? ' highlighted' : ''} ${groupType === 'unmapped' ? 'unmapped' : ''}`}
              onClick={() => handleClick(viewPosition)}
              disabled={groupType === 'unmapped'}
              title={viewPosition.field.name}
            >
              {viewPosition.field.name}
            </button>
            <div style={{ marginLeft: 40 }}>
              {!isRequired && viewPosition.field?.validationSettings.length > 0
                ? `${viewPosition.field?.validationSettings[0]?.errorSettings?.errorMessage}`
                : isRequired
                ? 'This field is required'
                : ''}
            </div>
          </React.Fragment>
        );

        previousGroupName = viewPosition.groupName;
      }
    };

    ['primary', 'secondary', 'unmapped'].forEach((groupType) => {
      renderGroup(fieldsViewPositions, groupType);
    });

    return menuItems;
  };

  return (
    <Popup
      className="validation-errors-dropdown-popup"
      trigger={
        <div className="item">
          <Button className="tertiary drop-down-button" onClick={handleErrorPopupClick}>
            <Menu text>
              <MenuItem className="error-triangle-menu-item" fitted>
                {hasErrorFields || hasMissingRequiredFields ? <ErrorTriangle /> : hasWarningFields ? <WarningTriangle /> : null}
                <Label
                  size="mini"
                  className={`error-triangle-numbers${numOfInvalidFields < 10 ? '' : numOfInvalidFields < 100 ? ' double-margin' : ' triple-margin'}`}
                  circular
                  floating
                >
                  <div className="error-triangle-number">{numOfInvalidFields > 99 ? '99+' : numOfInvalidFields}</div>
                </Label>
              </MenuItem>
            </Menu>
          </Button>
        </div>
      }
      basic
      offset={[3, -10]}
      position="bottom right"
      open={dropdownOpen}
      onClose={handlePopupClose}
      closeOnDocumentClick
      closeOnTriggerBlur={false}
      closeOnTriggerMouseLeave={false}
      closeOnPortalMouseLeave={false}
      style={{ padding: '0px' }}
    >
      <div className="validation-errors-dropdown">{renderMenu()}</div>
    </Popup>
  );
}
